import { useMemo, useState } from 'react';
import { CgArrowsExpandRight } from '@react-icons/all-files/cg/CgArrowsExpandRight';
import { REPORTS } from '@ampsec/platform-client';
import { CustomCard } from 'Components/molecules';
import { HealthMetrics } from 'Components/molecules';
import { IconWithTypography } from 'Components/molecules';
import { TOOLING_COVERAGE_GAPS, ACTIVE } from 'Core-utils/constants/constants';
import { getMetricsReportsData } from 'Apis/library';
import { Typography } from 'Components/atoms';
import { useApiObjectData } from 'Hooks/useApiObjectData';
import ErrorHandlerAndLoaderWrapper from 'Wrappers/ErrorHandlerandLoaderWrapper';
import { employeeToolingTransformerFunction, metricLabelColor } from 'Core-utils/helpers/ReportsHelper';
import toolingIcon from 'Assets/icons/toolingCoverage.svg';
import _ from 'lodash';
import { UsersInsightsModal } from 'Components/organisms';
import { useI18n } from 'Hooks/useI18n';
import './styles.scss';
import { MetricProps } from 'Components/molecules/HealthMetrics/HealthMetrics';
import { useSearchParamsState } from 'Hooks/useSearchParamsState';
import { store } from 'Rdx/store';
import { BaseMetric, BaseMetricResults } from 'Core-utils/types';

const COMPONENT_IDENTIFIER = 'Organization Tooling Coverage Gaps';

interface ToolingCoverageMetricsProps {
    employeeToolingData: Record<string, BaseMetric>;
    ToolingCoverageData: BaseMetricResults;
}

const buildHealthMetric = (healthMetric: BaseMetric, header: string) => {
    const metricValue = healthMetric?.value;
    const label = metricValue === null ? '-' : metricValue?.toString();
    const labelColor = metricLabelColor(metricValue);
    const value = (
        <div className="tooling-coverage__row-container-style d-flex flex-col">
            <div className="tooling-coverage__styled-space">
                <Typography variant="caption2" children={metricValue?.toString()} color="success-green" />
            </div>
        </div>
    );

    const valueVariant: 'caption2' | 'body2' = 'caption2';

    return { header, label, labelColor, value, valueVariant, isTotal: true };
};

const renderHealthMetrics = (metrics: MetricProps[]) => {
    const { t } = useI18n();
    metrics.sort((a, b) => {
        const labelA = parseInt(a?.label || '0');
        const labelB = parseInt(b?.label || '0');
        return labelB - labelA;
    });

    const topFourMetrics = metrics.slice(0, 4);
    return topFourMetrics.map((metric) => (
        <div className="tooling-coverage__endpoint-tooling-coverage" key={metric?.label} data-testid="health-metric">
            <HealthMetrics
                metric={metric ?? ({} as MetricProps)}
                hideValue={true}
                lowerLabel={t(ACTIVE)}
                headerClassName="tooling-coverage__header"
            />
        </div>
    ));
};

const ToolingCoverageMetrics = ({ employeeToolingData, ToolingCoverageData }: ToolingCoverageMetricsProps) => {
    const { t } = useI18n();
    const beforeVrContent = Object.keys(ToolingCoverageData)
        .slice(0, Object.keys(employeeToolingData).length + 1)
        .map((key) => {
            const healthMetric = ToolingCoverageData[key] ?? {
                label: '',
                value: 0,
            };
            const metric = buildHealthMetric(healthMetric, t(healthMetric.label));
            return metric;
        });

    const afterVrContent = Object.keys(ToolingCoverageData)
        .slice(Object.keys(employeeToolingData).length + 1)
        .map((key) => {
            const healthMetric = ToolingCoverageData[key] ?? {
                label: '',
                value: 0,
            };
            const metric = buildHealthMetric(healthMetric, t(healthMetric.label));
            return metric;
        });

    return (
        <div className="d-flex gap-2" data-testid="tooling-coverage-content">
            <div className="tooling-coverage__styled-container p-1">
                {beforeVrContent.map((metric) => (
                    <div className="tooling-coverage__endpoint-tooling-coverage" key={metric.label}>
                        <HealthMetrics
                            metric={metric}
                            hideValue={true}
                            lowerLabel={t('ACTIVE')}
                            headerClassName="tooling-coverage__header"
                        />
                    </div>
                ))}
            </div>
            {employeeToolingData && <div className="vr" />}
            <div className="tooling-coverage__styled-child align-items-end">{renderHealthMetrics(afterVrContent)}</div>
        </div>
    );
};

const ToolingCoverage = () => {
    const [show, setShow] = useState(false);
    const { setSearchParamsState } = useSearchParamsState();
    const {
        data: employeeToolingData,
        error: employeeToolingDataError,
        isLoading: employeeToolingDataisLoading,
        refresh,
    } = useApiObjectData(REPORTS.EMPLOYEE_TOOLING_COVERAGE, getMetricsReportsData, employeeToolingTransformerFunction, {
        organization: store.getState().orgModel.org,
        department: 'ALL',
    });

    const handleClose = () => {
        setSearchParamsState({});
        setShow(false);
    };
    const handleClick = () => {
        setShow(!show);
    };
    const {
        data: endpointToolingData,
        error: endpointToolingDataError,
        isLoading: endpointToolingDataisLoading,
    } = useApiObjectData<BaseMetricResults, BaseMetricResults>(
        REPORTS.ENDPOINT_TOOLING_COVERAGE,
        getMetricsReportsData,
        (a) => a,
        {
            organization: store.getState().orgModel.org,
            department: 'ALL',
        },
    );

    const toolingCoverageData = useMemo(
        () => ({
            ...employeeToolingData,
            ...endpointToolingData,
        }),
        [employeeToolingData, endpointToolingData],
    );

    return (
        <div className="tooling-coverage h-100" data-testid="tooling-coverage">
            <CustomCard
                cardType="twoThird"
                title={
                    <>
                        <IconWithTypography isRow={true} label={TOOLING_COVERAGE_GAPS} labelColor="primary" />
                        <CgArrowsExpandRight
                            size={22}
                            className="tooling-coverage__icon"
                            onClick={handleClick}
                            data-testid="expand-icon"
                        />
                    </>
                }
                content={
                    <div className="tooling-coverage__error-container">
                        <ErrorHandlerAndLoaderWrapper
                            error={employeeToolingDataError || endpointToolingDataError}
                            isLoading={employeeToolingDataisLoading || endpointToolingDataisLoading}
                            render={() => (
                                <ToolingCoverageMetrics
                                    ToolingCoverageData={toolingCoverageData}
                                    employeeToolingData={employeeToolingData}
                                />
                            )}
                            handleRefresh={refresh}
                            isEmpty={_.isEmpty(employeeToolingData) && _.isEmpty(endpointToolingData)}
                            ImageSrc={toolingIcon}
                            component={COMPONENT_IDENTIFIER}
                        />
                    </div>
                }
                onCardClick={handleClick}
            />
            {show && <UsersInsightsModal show={show} onClose={handleClose} title={TOOLING_COVERAGE_GAPS} />}
        </div>
    );
};

export default ToolingCoverage;
